import { vec3 } from 'gl-matrix';

/**
 * Authored defaults applied when a `FlameConfig` field is omitted. Exported
 * so the asset-manager preview pane (and any other code that mutates a Flame
 * in place rather than reconstructing it) can apply the same defaults the
 * constructor uses — without these, clearing an optional field in the UI
 * would leave the previously-typed value on the live Flame instead of
 * snapping back to the engine default.
 *
 * Values tuned for wood/wax fire — see Flame.ts constructor comment.
 */
export const FLAME_DEFAULT_WIDTH = 0.06;
export const FLAME_DEFAULT_HEIGHT = 0.18;
export const FLAME_DEFAULT_INTENSITY = 3.0;
export const FLAME_DEFAULT_BASE_COLOR: readonly [number, number, number] = [0.04, 0.1, 0.4];
export const FLAME_DEFAULT_MID_COLOR: readonly [number, number, number] = [1.0, 0.5, 0.1];
export const FLAME_DEFAULT_TIP_COLOR: readonly [number, number, number] = [0.65, 0.1, 0.02];
export const FLAME_DEFAULT_SCROLL_SPEED = 2.8;
export const FLAME_DEFAULT_TAPER = 1.2;
export const FLAME_DEFAULT_NOISE_SCALE = 3.5;
export const FLAME_DEFAULT_SOFTNESS = 0.05;

export type FlameConfig = {
  /** Anchor at the base of the flame, in world coordinates. */
  position: vec3 | readonly [number, number, number];
  /** Width of the flame at its base, in meters. */
  width?: number;
  /** Height from base to tip, in meters. */
  height?: number;
  /** HDR scalar applied to the gradient — drives bloom contribution. */
  intensity?: number;
  /** Cool base color (linear RGB). Default: saturated dark blue. */
  baseColor?: readonly [number, number, number];
  /** Warm mid color (linear RGB). Default: deep orange. */
  midColor?: readonly [number, number, number];
  /** Hot tip color (linear RGB). Default: dark red. */
  tipColor?: readonly [number, number, number];
  /** Vertical scroll rate of the noise — higher = faster flicker. */
  scrollSpeed?: number;
  /** Body taper exponent: 1 = cone, 2 = bullet, 0.7 = pillar-ish. */
  taper?: number;
  /** Spatial frequency of the noise across the body. */
  noiseScale?: number;
  /** Per-instance seed; varies the noise pattern between adjacent flames. */
  seed?: number;
  /**
   * Soft-particle fade radius in meters. Alpha ramps to 0 as the fragment
   * approaches scene depth — hides hard intersections with the candle wax /
   * campfire log poking through the billboard. 0 disables the fade.
   */
  softness?: number;
};

/**
 * A single procedural flame tongue. One Flame = one billboard quad rendered
 * by `FlamesPass` with additive blend. Position is the base anchor; the
 * billboard extends from there in world-up. Larger fires (campfires,
 * bonfires) are composed by placing several Flames around the source mesh.
 *
 * Not a `DrawableObject` — flames don't go through `objectManager`, picking,
 * or shadow casting. They're a thin data record that the dedicated pass
 * iterates each frame.
 */
export class Flame {
  public position: vec3;
  public width: number;
  public height: number;
  public intensity: number;
  public baseColor: [number, number, number];
  public midColor: [number, number, number];
  public tipColor: [number, number, number];
  public scrollSpeed: number;
  public taper: number;
  public noiseScale: number;
  public seed: number;
  public softness: number;

  constructor(config: FlameConfig) {
    this.position = vec3.fromValues(
      config.position[0],
      config.position[1],
      config.position[2],
    );
    // Defaults tuned for wood/wax fire — see FLAME_DEFAULT_* exports above.
    // Pushed away from yellow because candle / campfire flames read as orange
    // to the eye; pure yellow only shows up in gas / very-hot fires.
    this.width = config.width ?? FLAME_DEFAULT_WIDTH;
    this.height = config.height ?? FLAME_DEFAULT_HEIGHT;
    this.intensity = config.intensity ?? FLAME_DEFAULT_INTENSITY;
    this.baseColor = [...(config.baseColor ?? FLAME_DEFAULT_BASE_COLOR)];
    this.midColor = [...(config.midColor ?? FLAME_DEFAULT_MID_COLOR)];
    this.tipColor = [...(config.tipColor ?? FLAME_DEFAULT_TIP_COLOR)];
    this.scrollSpeed = config.scrollSpeed ?? FLAME_DEFAULT_SCROLL_SPEED;
    this.taper = config.taper ?? FLAME_DEFAULT_TAPER;
    this.noiseScale = config.noiseScale ?? FLAME_DEFAULT_NOISE_SCALE;
    this.seed = config.seed ?? Math.random() * 100;
    this.softness = config.softness ?? FLAME_DEFAULT_SOFTNESS;
  }
}
